import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import MapPicker from "@/components/Register/mapPicker.component";
import {
  INDUSTRY_TYPES,
  RESERVED_USER_CREATE_ID,
} from "@/constants/util.constants";
import { sendUpdateUserCompanies } from "@/models/socket/user.model";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Badge } from "@/shadcn/components/ui/badge";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn/components/ui/tabs";
import {
  DBBranchesByCompanyID,
  EditableBranch,
  EditableCompany,
} from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import { Building, MapPin, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";

type CompaniesDetailsFormProps = {
  initialDetails: DBBranchesByCompanyID[];
  fetchProfile: () => Promise<void>; // Function to refetch profile after saving changes
};

const mapInitialToEditable = (
  details: DBBranchesByCompanyID[]
): EditableCompany[] => {
  // This mapping is necessary to decouple DB structure from editable UI state
  return details.map((company) => ({
    id: company.company_id,
    accountNo: company.account_number || "",
    type: company.company_type,
    name: company.company_name,
    registrationNo: company.registration_number || "",
    vatNo: company.tax_number || "",
    industryType: company.industry_type,
    contactNumber: company.phone_number,
    branches: company.branches.map((branch) => ({
      id: branch.branch_id,
      name: branch.branch_name,
      addressLine1: branch.branch_address1,
      addressLine2: branch.branch_address2 || "",
      suburb: branch.branch_suburb || "",
      city: branch.branch_city,
      province: branch.branch_province || "",
      country: branch.branch_country || "",
      officeParkName: branch.branch_office_park_name || "",
      postalCode: branch.branch_pin_code,
      latitude: Number(branch.branch_latitude) || 0,
      longitude: Number(branch.branch_longitude) || 0,
    })),
  }));
};

const CompaniesDetailsForm: React.FC<CompaniesDetailsFormProps> = ({
  initialDetails,
  fetchProfile,
}) => {
  // Use local state for editing to avoid mutating props
  const [companies, setCompanies] = useState<EditableCompany[]>(
    mapInitialToEditable(initialDetails)
  );
  const [activeCompanyId, setActiveCompanyId] = useState<string>(
    companies[0]?.id || ""
  );
  const [activeBranchId, setActiveBranchId] = useState<string>(
    companies[0]?.branches[0]?.id || ""
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<string | null>(null);

  const updateCompany = (
    companyId: string,
    updates: Partial<EditableCompany>
  ) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === companyId ? { ...c, ...updates } : c))
    );
  };

  const updateBranch = (
    companyId: string,
    branchId: string,
    updates: Partial<EditableBranch>
  ) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? {
              ...c,
              branches: c.branches.map((b) =>
                b.id === branchId ? { ...b, ...updates } : b
              ),
            }
          : c
      )
    );
  };

  const addCompany = () => {
    // Use timestamp for unique id to avoid collisions
    const newCompany: EditableCompany = {
      id: `${RESERVED_USER_CREATE_ID}_company-${Date.now()}`,
      type: "business",
      name: "",
      registrationNo: "",
      vatNo: "",
      industryType: "Miscellaneous / Other",
      contactNumber: "",
      accountNo: "",
      branches: [
        {
          id: `${RESERVED_USER_CREATE_ID}_branch-${Date.now()}`,
          name: "Main Branch",
          addressLine1: "",
          addressLine2: "",
          suburb: "",
          city: "",
          province: "",
          country: "South Africa",
          postalCode: "",
          latitude: 0,
          longitude: 0,
        },
      ],
    };
    setCompanies([...companies, newCompany]);
    setActiveCompanyId(newCompany.id);
    setActiveBranchId(newCompany.branches[0].id);
  };

  const deleteCompany = (companyId: string) => {
    // Remove company and update active selection for UX continuity
    const updated = companies.filter((c) => c.id !== companyId);
    setCompanies(updated);
    if (activeCompanyId === companyId && updated.length > 0) {
      setActiveCompanyId(updated[0].id);
      setActiveBranchId(updated[0].branches[0]?.id || "");
    }
  };

  const addBranch = (companyId: string) => {
    const newBranch: EditableBranch = {
      id: `${RESERVED_USER_CREATE_ID}_branch-${Date.now()}`,
      name: "",
      addressLine1: "",
      addressLine2: "",
      suburb: "",
      city: "",
      province: "",
      country: "South Africa",
      postalCode: "",
      latitude: 0,
      longitude: 0,
    };
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId ? { ...c, branches: [...c.branches, newBranch] } : c
      )
    );
    setActiveBranchId(newBranch.id);
  };

  const deleteBranch = (companyId: string, branchId: string) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? {
              ...c,
              branches: c.branches.filter((b) => b.id !== branchId),
            }
          : c
      )
    );
  };

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const activeBranch = activeCompany?.branches.find(
    (b) => b.id === activeBranchId
  );
  const activeCompanyIsNewCompany = activeCompanyId.includes(
    RESERVED_USER_CREATE_ID
  );

  useEffect(() => {
    if (!activeCompany) {
      return;
    }
    if (activeCompany.type !== "private") {
      return;
    }

    const fieldsToUpdate: Partial<EditableCompany> = {};

    if (activeCompany.name !== "Private") {
      fieldsToUpdate.name = "Private";
    }

    if (activeCompany.industryType !== "Individual") {
      fieldsToUpdate.industryType = "Individual";
    }

    if (activeCompany.contactNumber) {
      fieldsToUpdate.contactNumber = "";
    }

    if (activeCompany.registrationNo) {
      fieldsToUpdate.registrationNo = undefined;
    }

    if (activeCompany.vatNo) {
      fieldsToUpdate.vatNo = undefined;
    }

    if (Object.keys(fieldsToUpdate).length === 0) {
      return; // No fields to update
    }

    updateCompany(activeCompany.id, fieldsToUpdate);
  }, [activeCompany?.type]);

  useEffect(() => {
    const foundCompany = companies.find((c) => c.id === activeCompanyId);

    if (!foundCompany) {
      return;
    }

    setActiveBranchId(
      foundCompany.branches.length > 0 ? foundCompany.branches[0].id : ""
    );
  }, [activeCompanyId]);

  async function saveChanges() {
    if (!validateForm()) {
      return; // Validation failed, do not proceed
    }
    setSubmitting(true);
    setErrors(null);
    const response = await sendUpdateUserCompanies(companies);
    setSubmitting(false);
    if (response.rStatus === "error") {
      genericFailureToast("Failed to update companies and branches");
      setErrors(response.rData);
      return;
    }

    genericSuccessToast("Companies and branches updated successfully");
    await fetchProfile(); // Refetch profile to get updated data
  }

  function handleCancel() {
    // Reset to initial state
    setCompanies(mapInitialToEditable(initialDetails));
    setActiveCompanyId(initialDetails[0]?.company_id || "");
    setActiveBranchId(initialDetails[0]?.branches[0]?.branch_id || "");
    setErrors(null);
  }

  function validateForm(): boolean {
    // Validation is required to prevent incomplete or invalid company/branch data from being submitted
    const newErrors: { companies?: string } = {};

    if (companies.length === 0) {
      newErrors.companies = "At least one company is required";
    } else {
      for (const company of companies) {
        // Company Name: required if business, max 150
        if (
          company.type === "business" &&
          (!company.name.trim() || company.name.length > 150)
        ) {
          newErrors.companies = "Company name is required (max 150 chars)";
          break;
        }
        // VAT: optional, max 20, must match format
        if (
          company.vatNo &&
          (!/^(\d{10}|[A-Za-z0-9-]{1,20})$/.test(company.vatNo) ||
            company.vatNo.length > 20)
        ) {
          newErrors.companies = "Invalid VAT number format (max 20 chars)";
          break;
        }
        // Industry Type: required, must be from predefined
        if (
          company.type === "business" &&
          (!company.industryType ||
            !INDUSTRY_TYPES.includes(company.industryType as any))
        ) {
          newErrors.companies = "Industry type is required";
          break;
        }
        // Company Contact Number: required, valid, max 15
        if (
          company.type === "business" &&
          (!company.contactNumber ||
            !/^\+?[0-9\s]{9,15}$/.test(company.contactNumber.trim()))
        ) {
          newErrors.companies =
            "Company contact number required and valid (max 15 chars)";
          break;
        }
        // registrationNo
        if (
          company.registrationNo &&
          (company.registrationNo.length > 50 ||
            !/^[A-Za-z0-9\- ]{1,50}$/.test(company.registrationNo))
        ) {
          newErrors.companies =
            "Registration number must be 50 characters or less and valid";
        }
        // vatNo
        if (
          company.vatNo &&
          (company.vatNo.length > 20 ||
            !/^(\d{10}|[A-Za-z0-9-]{1,20})$/.test(company.vatNo))
        ) {
          newErrors.companies =
            "VAT number must be 20 characters or less and valid";
        }
        // Branches: at least one, and all required address fields
        if (company.branches.length === 0) {
          newErrors.companies = "Each company must have at least one branch";
          break;
        }
        for (const branch of company.branches) {
          if (!branch.name || branch.name.length > 255) {
            newErrors.companies = "Branch name required (max 255 chars)";
            break;
          }

          // Street Address 1: required, max 255
          if (!branch.addressLine1 || branch.addressLine1.length > 255) {
            newErrors.companies = "Street Address 1 required (max 255 chars)";
            break;
          }
          // Street Address 2: optional, max 255
          if (branch.addressLine2 && branch.addressLine2.length > 255) {
            newErrors.companies = "Street Address 2 max 255 chars";
            break;
          }
          // Suburb: required, max 150
          if (!branch.suburb || branch.suburb.length > 150) {
            newErrors.companies = "Suburb required (max 150 chars)";
            break;
          }
          // City: required, max 100
          if (!branch.city || branch.city.length > 100) {
            newErrors.companies = "City required (max 100 chars)";
            break;
          }
          // Province: required, max 100
          if (!branch.province || branch.province.length > 100) {
            newErrors.companies = "Province required (max 100 chars)";
            break;
          }
          // Country: required, max 100
          if (!branch.country || branch.country.length > 100) {
            newErrors.companies = "Country required (max 100 chars)";
            break;
          }
          // Postal Code: required, max 10, valid format
          if (
            !branch.postalCode ||
            !/^[A-Za-z0-9\- ]{1,10}$/.test(branch.postalCode) ||
            branch.postalCode.length > 10
          ) {
            console.error("Postal code validation failed", branch.postalCode);
            newErrors.companies = "Postal code required, valid (max 10 chars)";
            break;
          }
        }
      }
    }

    setErrors(newErrors.companies || null);
    // Return true if no errors, false otherwise
    return !newErrors.companies;
  }

  return (
    <div>
      <Card className="m-auto max-w-4xl p-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            Update Companies & Branches
          </CardTitle>
        </CardHeader>
        <CardContent>
          {companies.length > 0 && (
            <Tabs value={activeCompanyId} onValueChange={setActiveCompanyId}>
              <TabsList className="flex h-auto min-h-fit w-full flex-wrap justify-start gap-2">
                {companies.map((company, idx) => (
                  <TabsTrigger
                    key={company.id}
                    value={company.id}
                    className="flex h-auto min-w-fit flex-shrink-0 cursor-pointer items-center gap-2 hover:!text-white"
                  >
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {company.name || `Company ${idx + 1}`}
                      {/* only delete if more than one company and it's a new company */}
                      {companies.length > 1 &&
                        company.id.includes(RESERVED_USER_CREATE_ID) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="ml-2 h-4 w-4 flex-shrink-0 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCompany(company.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
              {companies.map((company) => (
                <TabsContent
                  key={company.id}
                  value={company.id}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor={`company-type-${company.id}`}>
                      Company Type
                    </Label>
                    <Select
                      value={company.type}
                      onValueChange={(value) => {
                        if (value) {
                          updateCompany(company.id, {
                            type: value as "business" | "private",
                          });
                        }
                      }}
                      disabled={!activeCompanyIsNewCompany}
                    >
                      <SelectTrigger className="w-full max-w-full truncate">
                        <SelectValue placeholder="Select company type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {activeCompany?.type !== "private" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor={`company-name-${company.id}`}>
                          Company Name
                        </Label>
                        <Input
                          id={`company-name-${company.id}`}
                          value={company.name}
                          onChange={(e) =>
                            updateCompany(company.id, { name: e.target.value })
                          }
                          placeholder="Enter company name"
                          // disabled={activeCompany?.type === "private"}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`company-regno-${company.id}`}>
                          Registration Number
                        </Label>
                        <Input
                          id={`company-regno-${company.id}`}
                          value={company.registrationNo || ""}
                          onChange={(e) =>
                            updateCompany(company.id, {
                              registrationNo: e.target.value,
                            })
                          }
                          placeholder="Enter registration number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`company-vatno-${company.id}`}>
                          VAT Number
                        </Label>
                        <Input
                          id={`company-vatno-${company.id}`}
                          value={company.vatNo || ""}
                          onChange={(e) =>
                            updateCompany(company.id, { vatNo: e.target.value })
                          }
                          placeholder="Enter VAT number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`company-industry-${company.id}`}>
                          Industry Type
                        </Label>
                        <Select
                          value={company.industryType}
                          onValueChange={(value) =>
                            updateCompany(company.id, { industryType: value })
                          }
                        >
                          <SelectTrigger className="w-full max-w-full truncate">
                            <SelectValue placeholder="Select industry type" />
                          </SelectTrigger>
                          <SelectContent>
                            {INDUSTRY_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`company-contact-${company.id}`}>
                          Company Contact Number
                        </Label>
                        <Input
                          id={`company-contact-${company.id}`}
                          value={company.contactNumber}
                          onChange={(e) =>
                            updateCompany(company.id, {
                              contactNumber: e.target.value,
                            })
                          }
                          placeholder="Enter company contact number"
                        />
                      </div>
                    </>
                  )}

                  {/* Branches */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Branches</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {company.branches.map((branch) => (
                        <Badge
                          key={branch.id}
                          variant={
                            branch.id === activeBranchId
                              ? "default"
                              : "secondary"
                          }
                          className="flex cursor-pointer items-center gap-2"
                          onClick={() => setActiveBranchId(branch.id)}
                        >
                          <MapPin className="h-3 w-3" />
                          {branch.name || "Unnamed Branch"}
                          {/* Only show delete button if more than one branch and it's a new branch */}
                          {company.branches.length > 1 &&
                            branch.id.includes(RESERVED_USER_CREATE_ID) && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteBranch(company.id, branch.id);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                        </Badge>
                      ))}
                    </div>
                    {/* Active Branch Form */}
                    {activeBranch && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">
                            Branch Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor={`branch-name-${activeBranch.id}`}>
                              Branch Name
                            </Label>
                            <Input
                              id={`branch-name-${activeBranch.id}`}
                              value={activeBranch.name}
                              onChange={(e) =>
                                updateBranch(company.id, activeBranch.id, {
                                  name: e.target.value,
                                })
                              }
                              placeholder="Enter branch name"
                            />
                          </div>
                          <div className="space-y-2">
                            <ErrorBoundary>
                              <MapPicker
                                // Move all address fields into MapPicker so it manages address entry and autocomplete
                                address={activeBranch}
                                activeBranchID={activeBranch.id}
                                onAddressChange={(newAddress) => {
                                  updateBranch(company.id, activeBranch.id, {
                                    addressLine1: newAddress.addressLine1,
                                    addressLine2: newAddress.addressLine2,
                                    suburb: newAddress.suburb,
                                    city: newAddress.city,
                                    province: newAddress.province,
                                    country: newAddress.country,
                                    postalCode: newAddress.postalCode,
                                    latitude: newAddress.latitude,
                                    longitude: newAddress.longitude,
                                    officeParkName: newAddress.officeParkName,
                                  });
                                }}
                              />
                            </ErrorBoundary>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => addBranch(company.id)}
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Additional Branch
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              onClick={addCompany}
              variant="outline"
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Additional Company
            </Button>
          </div>
          {errors && (
            <Alert variant="destructive">
              <AlertDescription className="!text-red-500 opacity-95">
                {errors}
              </AlertDescription>
            </Alert>
          )}
          <div className="mt-4 flex justify-end gap-4">
            <Button
              type="button"
              className=""
              size="sm"
              variant="secondary"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveChanges}
              size="sm"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompaniesDetailsForm;
